import { mapNumber } from "@/helpers/utility/MathHelper";
import { cloneDeep } from "lodash-es";
import { GraphCurve } from "./GraphCurve";
import type { GraphPoint } from "./GraphPoint";

export class GraphModel {
  points: GraphPoint[];

  constructor(points: GraphPoint[]) {
    if (points.length == 0) {
      throw new Error("GraphModel needs at least one point");
    }
    this.points = points;
  }

  get curves() {
    const parts = [];

    const pointCopy = this.points.slice(0);

    while (pointCopy.length > 1) {
      const pStart = pointCopy.shift();
      const pEnd = pointCopy[0];

      if (pStart == null) continue;

      const part = new GraphCurve(pStart, pEnd);
      parts.push(part);
    }

    return parts;
  }

  get bounds() {
    let top = -100;
    let bottom = 100;
    let left = Infinity;
    let right = -Infinity;

    for (const point of this.points) {
      if (point.y > top) top = point.y;
      if (point.y < bottom) bottom = point.y;
      if (point.x < left) left = point.x;
      if (point.x > right) right = point.x;
    }
    return {
      top,
      bottom,
      left,
      right,
    };
  }

  get maxInfluence() {
    const { top, bottom } = this.bounds;
    if (Math.abs(top) > Math.abs(bottom)) return top;
    return bottom;
  }

  get absoluteMaxInfluence() {
    return Math.abs(this.maxInfluence);
  }

  get pStart() {
    return this.points[0];
  }

  get pEnd() {
    return this.points[this.points.length - 1];
  }

  get highestPoint() {
    let topPoint = this.points[1];
    for (const point of this.points) {
      if (point.y > topPoint.y) {
        topPoint = point;
      }
    }
    return topPoint;
  }
  get lowestPoint() {
    let bottomPoint = this.points[1];
    for (const point of this.points) {
      if (point.y < bottomPoint.y) {
        bottomPoint = point;
      }
    }
    return bottomPoint;
  }
  get extremePoints() {
    if (this.points.length === 0) return [];
    let extremePoint = this.points[1] ?? this.points[0];
    let top = extremePoint.y;
    let bottom = extremePoint.y;
    let extreme = Math.max(Math.abs(top), Math.abs(bottom));
    for (const point of this.points) {
      if (point.y > top && Math.abs(point.y) > Math.abs(bottom)) {
        top = point.y;
        extreme = top;
        extremePoint = point;
      } else if (point.y < bottom && Math.abs(point.y) > Math.abs(top)) {
        bottom = point.y;
        extreme = bottom;
        extremePoint = point;
      }
    }
    return this.points.filter((p) => Math.abs(p.y) === Math.abs(extreme));
  }

  getDescription({ yFactor = 1, xMin, xMax }: { yFactor?: number; xMin?: number | null; xMax?: number | null }) {
    if (this.curves.length === 0) return "";

    xMax = xMax ?? this.bounds.right;
    xMin = xMin ?? this.bounds.left;

    const p0 = this.pStart;
    let path = `M${mapNumber(p0.x, xMin, xMax, 0, 100)} ${p0.y / yFactor}`;

    for (const curve of this.curves) {
      path += curve.getDescription({ yFactor, xMin, xMax });
    }

    return path;
  }

  inverse() {
    const points = cloneDeep(this.points);
    for (const point of points) {
      point.y *= -1;
    }
    return points;
  }

  get inverted() {
    const { top, bottom } = this.bounds;
    if (Math.abs(bottom) > Math.abs(top)) return true;
    return false;
  }

  /**
   * Returns the y value at the given x coordinate
   */
  valueAtX(x: number) {
    if (this.points.length == 0) return 0;

    // if the first point is behind the x coordinate, return the y coordinate of the point.
    if (x <= this.pStart.x) {
      // if there is a bezier in, calculate the slope and return the value
      if (this.pStart.bezierIn && this.pStart.bezierIn.x != 0) {
        const slope = this.pStart.bezierIn.y / this.pStart.bezierIn.x;
        const distance = this.pStart.x - x;
        return this.pStart.y - slope * distance;
      }
      else return this.pStart.y;
    }
    // if the last point is before the x coordinate, return the y coordinate of the point.
    if (x >= this.pEnd.x) {
      // if there is a bezier out, calculate the slope and return the value
      if (this.pEnd.bezierOut && this.pEnd.bezierOut.x != 0) {
        const slope = this.pEnd.bezierOut.y / this.pEnd.bezierOut.x;
        const distance = x - this.pEnd.x;
        return this.pEnd.y + slope * distance;
      }
      else return this.pEnd.y;
    }
    // find the curve the point is on
    for (const curve of this.curves) {
      if (x >= curve.left && x <= curve.right) {
        const value = curve.valueAtX(x);
        if (value != null) return value;
      }
    }
    return 0;
  }

  /**
   * Returns the difference between two function values
   * @param x1 value before
   * @param x2 value after
   */
  getValueDifference(x1: number, x2: number) {
    const valueAfter = this.valueAtX(x2);
    const valueBefore = this.valueAtX(x1);
    return valueAfter - valueBefore;
  }

  /**
   * Returns the derivative at the given x coordinate
   */
  derivativeAtX(x: number, limitToDistance = false) {
    const startX = this.pStart.x;

    // if the first point is behind the x coordinate, the derivative should be 0.
    if (x < startX) {
      return 0;
    }
    // if the last point is before the x coordinate, the derivative should be 0.
    if (x > this.pEnd.x) {
      return 0;
    }

    // hack: fix an slope of infinity.
    if (x == this.pEnd.x) {
      x = x - 0.005;
    }

    // find the curve the point is on
    for (const curve of this.curves) {
      if (x >= curve.left && x <= curve.right) {
        const derivative = curve.derivativeAtX(x);
        if (derivative != null) {
          if (limitToDistance) {
            const maxDistance = Math.max(
              Math.abs(curve.pStart.y - curve.pEnd.y),
              Math.abs(curve.bezierStart.y),
              Math.abs(curve.bezierEnd.y)
            );
            const distance = Math.abs(derivative);
            if (distance > maxDistance) {
              return (derivative / distance) * maxDistance;
            }
          }
          return derivative;
        }
      }
    }
    return 0;
  }
}

export default GraphModel;
